/**
 * Pure math for the `<GaltonBoard>` marketing component — ball order, path
 * waypoints, settle timing, and landed-count derivation.
 *
 * WHY THIS FILE EXISTS (the prototype's confirmed Phase-7 defect):
 * the HTML prototype (`docs/Fable UiUx review/wtm-components-telemetry-
 * galton-anchor.html`, section 2) animated every ball with its own
 * setTimeout + rAF and incremented bin counters in callback order, so a
 * counter could tick out of sync with the visual settle, unbounded random
 * x-jitter could land a ball visually in a neighbouring bin while the
 * assigned bin got the count, and the timing soup made the final tally
 * unverifiable. This module fixes that STRUCTURALLY:
 *
 *  (a) each ball carries one precomputed `settleT`; `landedCountsAt(t)`
 *      counts exactly the balls with `settleT <= t`, and `positionAt`
 *      reports `landed` with the SAME comparison — the counter increment
 *      and the visual settle share one clock value, so a bin counter
 *      increments only at the exact frame its ball settles.
 *  (b) the final TWO waypoints of every path are clamped to the target
 *      bin's x-range, so the bin a ball visually lands in is always the
 *      bin that gets counted.
 *  (c) bin assignment is an exact multiset of the configured distribution
 *      (e.g. 24/36/20) — final counts ALWAYS equal the config.
 *  (d) split percentages derive from `landedCountsAt` only — never from
 *      released/airborne balls.
 *
 * No React, no DOM, seeded PRNG → fully deterministic and unit-testable
 * (tests/marketing/galton-math.test.ts). The component drives this math
 * from a `useStageDriver` progress value; reduced motion simply evaluates
 * everything at progress = 1.
 */

export interface GaltonDistribution {
  bull: number;
  base: number;
  bear: number;
}

/** 0 = bull, 1 = base, 2 = bear. */
export type GaltonBinIndex = 0 | 1 | 2;

export const GALTON_BIN_LABELS = ["BULL", "BASE", "BEAR"] as const;

/** SVG geometry (viewBox 0 0 460 310) — mirrors the approved prototype. */
export const GALTON_LAYOUT = {
  width: 460,
  height: 310,
  dropX: 230,
  dropY: 14,
  pegRows: 5,
  pegTop: 50,
  pegRowHeight: 30,
  pegSpacing: 34,
  pegBaseCount: 7,
  binCenters: [120, 230, 340],
  /** Inner [left, right] x-range of each bin (divider walls at 180 / 280). */
  binRanges: [
    [60, 180],
    [180, 280],
    [280, 400],
  ],
  binEntryY: 235,
  floorY: 290,
  dotRadius: 2.6,
  stackCols: 12,
  stackColSpacing: 7,
  stackRowSpacing: 6,
} as const;

/** Ball centers keep this margin inside walls/dividers. */
const BIN_MARGIN = GALTON_LAYOUT.dotRadius + 0.4;

export interface GaltonBall {
  /** Drop order, 0-based. */
  index: number;
  /** Assigned bin — by construction also the visually landed bin. */
  bin: GaltonBinIndex;
  /** Stage progress (0..1) at which the ball is released. */
  releaseT: number;
  /** Stage progress (0..1) of the EXACT frame the ball settles. */
  settleT: number;
  /** k-th ball to land in its bin — determines its resting stack slot. */
  stackIndex: number;
  /**
   * Path waypoints [x, y], top → rest. The last two (bin entry + stack
   * slot) are clamped to the assigned bin's x-range — defect fix (b).
   */
  waypoints: ReadonlyArray<readonly [number, number]>;
}

export interface GaltonRun {
  balls: GaltonBall[];
  total: number;
  /** [bull, base, bear] — the configured target counts. */
  target: [number, number, number];
  /** Fraction of the drop stage one ball spends falling. */
  fallFraction: number;
}

export interface BuildGaltonRunOptions {
  /** PRNG seed — fixed default so SSR, client, and tests agree. */
  seed?: number;
  /** Fraction of the drop stage one ball spends airborne. Default 0.14. */
  fallFraction?: number;
}

/** Small, fast, deterministic PRNG (mulberry32). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function clampToBin(x: number, bin: GaltonBinIndex): number {
  const [left, right] = GALTON_LAYOUT.binRanges[bin];
  return Math.min(Math.max(x, left + BIN_MARGIN), right - BIN_MARGIN);
}

/** Resting [x, y] of the `stackIndex`-th ball landed in `bin`. */
export function stackPosition(
  bin: GaltonBinIndex,
  stackIndex: number
): readonly [number, number] {
  const L = GALTON_LAYOUT;
  const col = stackIndex % L.stackCols;
  const row = Math.floor(stackIndex / L.stackCols);
  const x = clampToBin(
    L.binCenters[bin] + (col - (L.stackCols - 1) / 2) * L.stackColSpacing,
    bin
  );
  const y = L.floorY - 2 - row * L.stackRowSpacing;
  return [x, y];
}

function buildWaypoints(
  bin: GaltonBinIndex,
  stackIndex: number,
  rnd: () => number
): Array<readonly [number, number]> {
  const L = GALTON_LAYOUT;
  const wallLeft = L.binRanges[0][0] + BIN_MARGIN;
  const wallRight = L.binRanges[2][1] - BIN_MARGIN;
  const pts: Array<readonly [number, number]> = [[L.dropX, L.dropY]];

  // Plinko walk through the peg rows, biased toward the assigned bin.
  // The bias divisor shrinks to 1 on the last row, so the walk converges
  // on the bin center (± half a peg gap of jitter) before the bins begin.
  let x: number = L.dropX;
  for (let r = 0; r < L.pegRows; r++) {
    const bias = (L.binCenters[bin] - x) / (L.pegRows - r);
    x += bias + (rnd() - 0.5) * 26;
    x = Math.min(Math.max(x, wallLeft), wallRight); // never exits the board
    pts.push([x, L.pegTop + r * L.pegRowHeight + 8]);
  }

  // Defect fix (b): the final TWO waypoints are clamped to the target
  // bin's x-range, so the visual landing bin is always the counted bin.
  pts.push([clampToBin(L.binCenters[bin] + (rnd() - 0.5) * 30, bin), L.binEntryY]);
  pts.push(stackPosition(bin, stackIndex));
  return pts;
}

/**
 * Build the full deterministic run: exact bin multiset (defect fix c),
 * seeded-shuffle drop order, per-ball release/settle times and waypoints.
 */
export function buildGaltonRun(
  distribution: GaltonDistribution,
  options: BuildGaltonRunOptions = {}
): GaltonRun {
  const { seed = 7, fallFraction = 0.14 } = options;
  const target: [number, number, number] = [
    Math.max(0, Math.round(distribution.bull)),
    Math.max(0, Math.round(distribution.base)),
    Math.max(0, Math.round(distribution.bear)),
  ];
  const total = target[0] + target[1] + target[2];
  const rnd = mulberry32(seed);

  // Exact multiset of bin assignments → final counts == config, always.
  const order: GaltonBinIndex[] = [];
  target.forEach((n, b) => {
    for (let i = 0; i < n; i++) order.push(b as GaltonBinIndex);
  });
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }

  // Releases span [0, 1 - fallFraction] so the LAST ball settles at
  // exactly progress 1 — the stage boundary.
  const releaseSpan = Math.max(0, 1 - fallFraction);
  const stackCounters: [number, number, number] = [0, 0, 0];
  const balls: GaltonBall[] = order.map((bin, index) => {
    const releaseT = total > 1 ? (index / (total - 1)) * releaseSpan : 0;
    const stackIndex = stackCounters[bin]++;
    return {
      index,
      bin,
      releaseT,
      settleT: releaseT + fallFraction,
      stackIndex,
      waypoints: buildWaypoints(bin, stackIndex, rnd),
    };
  });

  return { balls, total, target, fallFraction };
}

export interface GaltonBallFrame {
  x: number;
  y: number;
  /** False until the ball's releaseT — render it invisible. */
  released: boolean;
  /**
   * True from the EXACT frame `stageProgress >= settleT` — the same
   * comparison `landedCountsAt` uses, so visual settle and counter
   * increment can never diverge (defect fix a).
   */
  landed: boolean;
}

/** Pure: a ball's position at drop-stage progress `stageProgress` (0..1). */
export function positionAt(ball: GaltonBall, stageProgress: number): GaltonBallFrame {
  const { releaseT, settleT, waypoints } = ball;
  const last = waypoints[waypoints.length - 1];
  if (stageProgress >= settleT) {
    return { x: last[0], y: last[1], released: true, landed: true };
  }
  if (stageProgress < releaseT) {
    return { x: waypoints[0][0], y: waypoints[0][1], released: false, landed: false };
  }
  const span = settleT - releaseT;
  const local = span > 0 ? (stageProgress - releaseT) / span : 1;
  const eased = local * local; // gravity-like acceleration
  const segs = waypoints.length - 1;
  const f = Math.min(eased * segs, segs - 1e-9);
  const i = Math.floor(f);
  const lp = f - i;
  return {
    x: waypoints[i][0] + (waypoints[i + 1][0] - waypoints[i][0]) * lp,
    y: waypoints[i][1] + (waypoints[i + 1][1] - waypoints[i][1]) * lp,
    released: true,
    landed: false,
  };
}

/**
 * Defect fix (a) + (c): per-bin counts of balls that have VISUALLY settled
 * by `stageProgress` — `settleT <= t`, the same comparison `positionAt`
 * uses for `landed`. At t = 1 this equals the configured distribution.
 */
export function landedCountsAt(
  balls: GaltonBall[],
  stageProgress: number
): [number, number, number] {
  const counts: [number, number, number] = [0, 0, 0];
  for (const b of balls) {
    if (stageProgress >= b.settleT) counts[b.bin]++;
  }
  return counts;
}

/** Number of balls released (visible) by `stageProgress`. */
export function releasedCountAt(balls: GaltonBall[], stageProgress: number): number {
  let n = 0;
  for (const b of balls) {
    if (stageProgress >= b.releaseT) n++;
  }
  return n;
}

/**
 * Defect fix (d): split percentages from LANDED counts only. Returns null
 * while nothing has landed (render a placeholder, not a fake 0%).
 */
export function splitFromCounts(
  counts: readonly [number, number, number]
): [number, number, number] | null {
  const tot = counts[0] + counts[1] + counts[2];
  if (tot === 0) return null;
  return [
    Math.round((counts[0] / tot) * 100),
    Math.round((counts[1] / tot) * 100),
    Math.round((counts[2] / tot) * 100),
  ];
}

/** Index of the bin with the most balls (ties → earlier bin). */
export function leadingBin(counts: readonly number[]): GaltonBinIndex {
  let best = 0;
  for (let i = 1; i < 3; i++) {
    if ((counts[i] ?? 0) > (counts[best] ?? 0)) best = i;
  }
  return best as GaltonBinIndex;
}
